import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BanknoteIcon, CopyIcon, DownloadIcon, FileMinusIcon, MoreHorizontalIcon, PencilIcon, PlusIcon, ReceiptIcon, SendIcon } from 'lucide-react';
import { CompanySettings, ProInvoice, ProInvoiceStatus } from '../../types/pro';
import { INVOICE_STATUS, formatDate, kr } from '../../utils/pro';
import { invoiceAmounts } from '../../utils/invoice';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProInvoicesProps {
  invoices: ProInvoice[];
  company: CompanySettings;
  onOpen: (invoiceId: string) => void;
  onNew: () => void;
  onCopy: (invoice: ProInvoice) => void;
  onCreditNote: (invoice: ProInvoice) => void;
}
type Filter = 'alle' | 'kladder' | 'sendt' | 'betalt' | 'forfalden';
const FILTERS: {
  id: Filter;
  label: string;
}[] = [{
  id: 'alle',
  label: 'Alle'
}, {
  id: 'kladder',
  label: 'Kladder'
}, {
  id: 'sendt',
  label: 'Sendt'
}, {
  id: 'betalt',
  label: 'Betalt'
}, {
  id: 'forfalden',
  label: 'Forfalden'
}];
const matchesFilter = (invoice: ProInvoice, filter: Filter): boolean => {
  if (filter === 'alle') return true;
  if (filter === 'kladder') return invoice.status === 'kladde' || invoice.status === 'godkendt';
  return invoice.status === filter as ProInvoiceStatus;
};
export function ProInvoices({
  invoices,
  company,
  onOpen,
  onNew,
  onCopy,
  onCreditNote
}: ProInvoicesProps) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('alle');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuFor(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return invoices.filter(invoice => matchesFilter(invoice, filter)).filter(invoice => term ? [invoice.invoiceNo, invoice.customerName, invoice.reference].filter(Boolean).join(' ').toLowerCase().includes(term) : true).sort((a, b) => b.invoiceDate.localeCompare(a.invoiceDate));
  }, [invoices, query, filter]);
  const overdue = invoices.filter(item => item.status === 'forfalden');
  const outstanding = invoices.filter(item => ['sendt', 'forfalden', 'godkendt'].includes(item.status)).reduce((sum, item) => sum + invoiceAmounts(item).outstanding, 0);
  const actions = (invoice: ProInvoice) => [{
    label: 'Rediger',
    icon: PencilIcon,
    run: () => invoice.id && onOpen(invoice.id)
  }, {
    label: 'Kopier faktura',
    icon: CopyIcon,
    run: () => onCopy(invoice)
  }, {
    label: 'Download PDF',
    icon: DownloadIcon,
    run: () => invoice.id && onOpen(invoice.id)
  }, {
    label: invoice.status === 'sendt' ? 'Send igen' : 'Send faktura',
    icon: SendIcon,
    run: () => invoice.id && onOpen(invoice.id)
  }, {
    label: 'Registrer betaling',
    icon: BanknoteIcon,
    run: () => invoice.id && onOpen(invoice.id)
  }, {
    label: 'Opret kreditnota',
    icon: FileMinusIcon,
    run: () => onCreditNote(invoice),
    danger: true
  }];
  return <div data-id="element-1563" data-mp="3ujfpldr95h8n">
      <PageHeader title="Fakturaer" subtitle={`${kr(outstanding)} udestående${overdue.length > 0 ? ` · ${overdue.length} forfaldne` : ''}`} actions={<button onClick={onNew} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1565" data-mp="31yp90ysc3gvg">
            <PlusIcon className="h-4 w-4" data-id="element-1566" data-mp="259ez4vytyzq3" />
            Ny faktura
          </button>} data-id="element-1564" data-mp="3uj5u5anfuhvk" />

      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1567" data-mp="29biazlpew6bw">
        <SearchField value={query} onChange={setQuery} placeholder="Søg fakturanummer, kunde eller reference…" className="min-w-[220px] flex-1" data-id="element-1568" data-mp="2995ocv8pl7si" />
        <div role="group" aria-label="Filtrer fakturaer" className="flex rounded-lg border border-stone-200 bg-white p-1" data-id="element-1569" data-mp="2j0tgyzh04uvk">
          {FILTERS.map(option => <button key={option.id} onClick={() => setFilter(option.id)} aria-pressed={filter === option.id} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === option.id ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1570" data-mp="2sx4xy2rje8wc">
              {option.label}
            </button>)}
        </div>
      </div>

      {rows.length === 0 ? <EmptyState icon={ReceiptIcon} title="Ingen fakturaer matcher" description="Prøv et andet filter, eller opret en ny faktura." data-id="element-1571" data-mp="lcprn8mzz369" /> : <>
          {/* Desktop table */}
          <div className="hidden overflow-visible rounded-xl border border-stone-200 bg-white md:block" data-id="element-1572" data-mp="1e1mxgca0v7i4">
            <table className="w-full text-sm" data-id="element-1573" data-mp="193v4rg1jzhcv">
              <thead data-id="element-1574" data-mp="150ytkaeceniy">
                <tr className="border-b border-stone-200 bg-stone-50 text-left" data-id="element-1575" data-mp="150fc0uslu15g">
                  {['Fakturanr.', 'Kunde', 'Dato', 'Forfald', 'Beløb', 'Status'].map(heading => <th key={heading} scope="col" className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1576" data-mp="3krb9336zn9s6">
                      {heading}
                    </th>)}
                  <th scope="col" className="w-12" data-id="element-1577" data-mp="3d8tx8qbn3iw1" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-id="element-1578" data-mp="3h941nyjsa18s">
                {rows.map(invoice => {
              const sums = invoiceAmounts(invoice);
              const isOverdue = invoice.status === 'forfalden';
              return <tr key={invoice.id} className={`transition-colors hover:bg-stone-50 ${isOverdue ? 'bg-red-50/40' : ''}`} data-id="element-1579" data-mp="2tlmgwmyxs3fb">
                      <td onClick={() => invoice.id && onOpen(invoice.id)} className="cursor-pointer whitespace-nowrap px-4 py-3 font-mono text-xs text-stone-500" data-id="element-1580" data-mp="2kky1bntw9v0b">
                        {invoice.invoiceNo}
                      </td>
                      <td onClick={() => invoice.id && onOpen(invoice.id)} className="cursor-pointer px-4 py-3 font-medium text-stone-900" data-id="element-1581" data-mp="210h2i6wo4tfp">
                        {invoice.customerName || '—'}
                        {invoice.creditNoteOf && <span className="ml-2 text-xs font-normal text-stone-400" data-id="element-1582" data-mp="2ou8mscu7dl40">
                            kreditnota
                          </span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-stone-500" data-id="element-1583" data-mp="2tnbk8w23peph">
                        {formatDate(invoice.invoiceDate)}
                      </td>
                      <td className={`whitespace-nowrap px-4 py-3 ${isOverdue ? 'font-semibold text-red-600' : 'text-stone-500'}`} data-id="element-1584" data-mp="37nmem92mdbfe">
                        {formatDate(invoice.dueDate)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums text-stone-900" data-id="element-1585" data-mp="mvhjbv9k7quh">
                        {kr(sums.total)}
                        {sums.paid > 0 && sums.outstanding > 0 && <span className="block text-xs font-normal text-stone-400" data-id="element-1586" data-mp="3rbir9xehyw66">
                            {kr(sums.outstanding)} udestår
                          </span>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1587" data-mp="43f1igl6m4in">
                        <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-1588" data-mp="8x02mhi2nem2" />
                      </td>
                      <td className="relative px-2 py-3" data-id="element-1589" data-mp="37kxa4q8s3fk4">
                        <button onClick={() => setMenuFor(menuFor === invoice.id ? null : invoice.id ?? null)} aria-label={`Flere handlinger for ${invoice.invoiceNo}`} aria-expanded={menuFor === invoice.id} className="rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-1590" data-mp="3cl1pgcxya48r">
                          <MoreHorizontalIcon className="h-4 w-4" data-id="element-1591" data-mp="2hbfzylk1p2i1" />
                        </button>

                        {menuFor === invoice.id && <div ref={menuRef} className="absolute right-2 top-full z-30 w-52 overflow-hidden rounded-xl border border-stone-200 bg-white py-1 shadow-lg" data-id="element-1592" data-mp="2cascuayxt5t3">
                            {actions(invoice).map(action => {
                      const Icon = action.icon;
                      return <button key={action.label} onClick={() => {
                        action.run();
                        setMenuFor(null);
                      }} className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-stone-50 ${action.danger ? 'text-red-600' : 'text-stone-700'}`} data-id="element-1593" data-mp="agxvg1d79mrg">
                                  <Icon className="h-4 w-4 flex-shrink-0 opacity-60" data-id="element-1594" data-mp="1eonnj9bg7hr" />
                                  {action.label}
                                </button>;
                    })}
                          </div>}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>

          {/* Phone — rows become cards */}
          <ul className="space-y-2 md:hidden" data-id="element-1595" data-mp="1bfau6421abg8">
            {rows.map(invoice => {
          const sums = invoiceAmounts(invoice);
          return <li key={invoice.id} data-id="element-1596" data-mp="235ffc4xmb2ii">
                  <button onClick={() => invoice.id && onOpen(invoice.id)} className="w-full rounded-xl border border-stone-200 bg-white p-4 text-left" data-id="element-1597" data-mp="236gr6bmgjt1a">
                    <span className="flex items-center justify-between gap-3" data-id="element-1598" data-mp="1ucltfono14vo">
                      <span className="font-mono text-xs text-stone-400" data-id="element-1599" data-mp="1ydnjpfzlu6li">
                        {invoice.invoiceNo}
                      </span>
                      <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-1600" data-mp="2m2girlbiwukc" />
                    </span>
                    <span className="mt-1.5 block truncate font-medium text-stone-900" data-id="element-1601" data-mp="2qwujm38yftwe">
                      {invoice.customerName || '—'}
                    </span>
                    <span className="mt-1 flex items-center justify-between gap-3" data-id="element-1602" data-mp="2wpwnqg2wv5u4">
                      <span className="text-xs text-stone-500" data-id="element-1603" data-mp="2rp9106lptb5p">
                        Forfalder {formatDate(invoice.dueDate)}
                      </span>
                      <span className="font-semibold tabular-nums text-stone-900" data-id="element-1604" data-mp="2hs4bbwsnuxk5">
                        {kr(sums.total)}
                      </span>
                    </span>
                  </button>
                </li>;
        })}
          </ul>
        </>}
    </div>;
}